import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef, useState } from 'react'
import {
  paymentsQueries,
  useConfirmBillPayment,
  useDeleteBillPaymentInvoiceAttachment,
  useResendBillPaymentAuthorization,
  useRestartBillPaymentAuthorization,
  useUploadBillPaymentInvoiceAttachment,
} from 'src/queries/payments/payments.queries'
import {
  BillPaymentDetailsResponse,
  BillPaymentError,
  BillPaymentInvoiceAttachmentResponse,
  BillPaymentResponse,
  ConfirmBillPaymentRequest,
  ResendBillPaymentAuthorizationAttempt,
  RestartBillPaymentAuthorizationAttempt,
} from 'src/queries/payments/payments.types'
import {
  createBillPaymentIdempotencyKey,
  shouldReconcileBillPaymentError,
} from 'src/queries/payments/payments.utils'

import {
  BillPaymentDraftInput,
  buildConfirmBillPaymentRequest,
  sameInvoiceFileReference,
} from './billPaymentForm'

type UploadAttempt = {
  file: File
  idempotencyKey: string
  attachment?: BillPaymentInvoiceAttachmentResponse
}

type ConfirmAttempt = {
  request: ConfirmBillPaymentRequest
  idempotencyKey: string
}

type BillPaymentFlowPhase =
  | 'editable'
  | 'confirming'
  | 'confirm-unknown'
  | 'awaiting-authorization'
  | 'reconciling'
  | 'schedule-unknown'
  | 'terminal'

export type BillPaymentDeliveryUpdate = Pick<
  NonNullable<BillPaymentDetailsResponse['authorization']>,
  'deliveryState' | 'destinationHint'
> & { sequence: number }

export type BillPaymentReconciliationResult =
  | { kind: 'scheduled'; details: BillPaymentDetailsResponse }
  | {
      kind: 'authorization'
      details: BillPaymentDetailsResponse
      sameAttempt: boolean
    }
  | { kind: 'terminal'; details: BillPaymentDetailsResponse }
  | { kind: 'unavailable'; error: BillPaymentError }

export type BillPaymentSecondaryActionResult =
  | { kind: 'updated'; details: BillPaymentDetailsResponse }
  | { kind: 'unchanged'; details: BillPaymentDetailsResponse }
  | { kind: 'expired'; details: BillPaymentDetailsResponse }
  | { kind: 'terminal'; details: BillPaymentDetailsResponse }
  | { kind: 'unavailable'; error: BillPaymentError }

type FlowState = {
  phase: BillPaymentFlowPhase
  details?: BillPaymentDetailsResponse
  error?: BillPaymentError
  isMfaOpen: boolean
  isSealed: boolean
  deliveryUpdate?: BillPaymentDeliveryUpdate
}

const initialState: FlowState = {
  phase: 'editable',
  isMfaOpen: false,
  isSealed: false,
}

const malformedResponseError = (withoutPaymentId = false): BillPaymentError => ({
  error: withoutPaymentId
    ? 'The payment confirmation response did not identify a payment to recover.'
    : 'The payment confirmation response is incomplete.',
  errorCode: 'PAYMENT_CONFIRM_RESPONSE_INVALID',
})

const isNotFound = (error: BillPaymentError): boolean => error.status === 404

const authorizationSnapshot = (
  details: BillPaymentDetailsResponse,
): string => {
  const authorization = details.authorization
  return JSON.stringify([
    details.payment.id,
    details.payment.revision,
    authorization?.authorizationId,
    authorization?.state,
    authorization?.expiresAt,
    authorization?.attemptsRemaining,
    authorization?.resendAvailableAt,
    authorization?.deliveryState,
    authorization?.destinationHint,
  ])
}

export interface UseBillPaymentFlowResult {
  phase: BillPaymentFlowPhase
  details?: BillPaymentDetailsResponse
  error?: BillPaymentError
  isMfaOpen: boolean
  isSealed: boolean
  deliveryUpdate?: BillPaymentDeliveryUpdate
  isInteractionLocked: boolean
  confirm: (draft: BillPaymentDraftInput, file: File | null) => Promise<void>
  openMfa: () => void
  closeMfa: () => void
  resend: () => Promise<BillPaymentSecondaryActionResult>
  restart: () => Promise<BillPaymentSecondaryActionResult>
  reconcile: () => Promise<BillPaymentDetailsResponse>
  reconcileSchedule: (
    error: BillPaymentError,
  ) => Promise<BillPaymentReconciliationResult>
  discardUnattachedFile: (nextFile: File | null) => Promise<void>
  acceptScheduled: (payment: BillPaymentResponse) => void
  acceptTerminalError: (error: BillPaymentError) => Promise<void>
  reset: () => void
}

/**
 * Owns one opened Pay Bill session. It deliberately has no flyout dismissal or
 * toast policy: callers use `isInteractionLocked` and interpret terminal
 * reconciliation results in their own UI.
 */
export default function useBillPaymentFlow(
  organizationId: string | undefined,
): UseBillPaymentFlowResult {
  const queryClient = useQueryClient()
  const upload = useUploadBillPaymentInvoiceAttachment()
  const removeAttachment = useDeleteBillPaymentInvoiceAttachment(organizationId)
  const confirmPayment = useConfirmBillPayment(organizationId)
  const resendAuthorization = useResendBillPaymentAuthorization(organizationId)
  const restartAuthorization = useRestartBillPaymentAuthorization(organizationId)
  const [state, setState] = useState<FlowState>(initialState)
  const stateRef = useRef(state)
  const sessionRef = useRef(0)
  const cleanupOperationRef = useRef(0)
  const deliverySequenceRef = useRef(0)
  const uploadAttemptRef = useRef<UploadAttempt | null>(null)
  const confirmAttemptRef = useRef<ConfirmAttempt | null>(null)
  const resendAttemptRef =
    useRef<ResendBillPaymentAuthorizationAttempt | null>(null)
  const restartAttemptRef =
    useRef<RestartBillPaymentAuthorizationAttempt | null>(null)

  stateRef.current = state

  const replaceState = useCallback((next: FlowState): void => {
    stateRef.current = next
    setState(next)
  }, [])

  const updateState = useCallback(
    (patch: Partial<FlowState>): void => {
      replaceState({ ...stateRef.current, ...patch })
    },
    [replaceState],
  )

  const discardUnattachedFile = useCallback(
    async (nextFile: File | null): Promise<void> => {
      const attempt = uploadAttemptRef.current
      if (
        !attempt ||
        (nextFile && sameInvoiceFileReference(attempt.file, nextFile))
      ) {
        return
      }

      const session = sessionRef.current
      const operation = ++cleanupOperationRef.current

      if (attempt.attachment) {
        try {
          await removeAttachment.mutateAsync(attempt.attachment.id)
        } catch (caughtError) {
          const error = caughtError as BillPaymentError
          if (!isNotFound(error)) throw error
        }
      }

      if (
        session === sessionRef.current &&
        operation === cleanupOperationRef.current &&
        uploadAttemptRef.current === attempt
      ) {
        uploadAttemptRef.current = null
      }
    },
    [removeAttachment],
  )

  const reconcileDetails = useCallback(
    async (
      details: BillPaymentDetailsResponse,
      session: number,
      mode: 'default' | 'schedule' | 'secondary' = 'default',
      cause?: BillPaymentError,
    ): Promise<BillPaymentReconciliationResult> => {
      replaceState({
        ...stateRef.current,
        phase: 'reconciling',
        details,
        error: undefined,
      })

      let authoritative: BillPaymentDetailsResponse
      try {
        authoritative = await queryClient.fetchQuery(
          paymentsQueries.paymentDetail(organizationId, details.payment.id),
        )
      } catch (caughtError) {
        if (session !== sessionRef.current) {
          return { kind: 'terminal', details }
        }
        const error = caughtError as BillPaymentError
        if (mode === 'schedule') {
          replaceState({
            ...stateRef.current,
            phase: 'schedule-unknown',
            details,
            error: cause ?? error,
            isMfaOpen: true,
            isSealed: true,
          })
          return { kind: 'unavailable', error }
        }
        if (mode === 'secondary') {
          replaceState({
            ...stateRef.current,
            phase: 'awaiting-authorization',
            details,
            error: cause ?? error,
            isMfaOpen: true,
            isSealed: true,
          })
          return { kind: 'unavailable', error }
        }
        replaceState({
          ...stateRef.current,
          phase: 'terminal',
          error: caughtError as BillPaymentError,
          isMfaOpen: false,
          isSealed: true,
        })
        throw caughtError
      }

      if (session !== sessionRef.current) {
        return { kind: 'terminal', details: authoritative }
      }

      const authorization = authoritative.authorization
      if (authoritative.payment.state === 'SCHEDULED') {
        resendAttemptRef.current = null
        restartAttemptRef.current = null
        replaceState({
          phase: 'terminal',
          details: authoritative,
          error: undefined,
          isMfaOpen: false,
          isSealed: true,
        })
        return { kind: 'scheduled', details: authoritative }
      } else if (
        authoritative.payment.state === 'AWAITING_AUTHORIZATION' &&
        authorization &&
        (authorization.state === 'PENDING' || authorization.state === 'EXPIRED')
      ) {
        const sameAttempt =
          authoritative.payment.id === details.payment.id &&
          authoritative.payment.revision === details.payment.revision &&
          authorization.authorizationId === details.authorization?.authorizationId
        if (!(mode !== 'default' && sameAttempt)) {
          resendAttemptRef.current = null
          restartAttemptRef.current = null
        }
        replaceState({
          phase:
            mode === 'schedule' && sameAttempt
              ? 'schedule-unknown'
              : 'awaiting-authorization',
          details: authoritative,
          error: mode === 'schedule' && sameAttempt ? cause : undefined,
          isMfaOpen:
            authorization.state === 'EXPIRED' ||
            mode !== 'default' ||
            stateRef.current.isMfaOpen,
          isSealed: true,
        })
        return { kind: 'authorization', details: authoritative, sameAttempt }
      } else {
        resendAttemptRef.current = null
        restartAttemptRef.current = null
        replaceState({
          phase: 'terminal',
          details: authoritative,
          error: undefined,
          isMfaOpen: false,
          isSealed: true,
        })
        return { kind: 'terminal', details: authoritative }
      }
    },
    [organizationId, queryClient, replaceState],
  )

  const confirm = useCallback(
    async (draft: BillPaymentDraftInput, file: File | null): Promise<void> => {
      if (
        stateRef.current.isSealed ||
        stateRef.current.phase === 'confirming'
      ) {
        return
      }
      const session = sessionRef.current
      updateState({ phase: 'confirming', error: undefined })

      let attachment: BillPaymentInvoiceAttachmentResponse | undefined
      try {
        if (file) {
          if (
            uploadAttemptRef.current &&
            !sameInvoiceFileReference(uploadAttemptRef.current.file, file)
          ) {
            await discardUnattachedFile(file)
          }

          let uploadAttempt = uploadAttemptRef.current
          if (!uploadAttempt) {
            uploadAttempt = {
              file,
              idempotencyKey: createBillPaymentIdempotencyKey(),
            }
            uploadAttemptRef.current = uploadAttempt
          }

          attachment = uploadAttempt.attachment
          if (!attachment) {
            attachment = await upload.mutateAsync(uploadAttempt)
            if (session !== sessionRef.current) return
            uploadAttempt.attachment = attachment
          }
        } else if (uploadAttemptRef.current) {
          await discardUnattachedFile(null)
        }
      } catch (caughtError) {
        if (session !== sessionRef.current) return
        const error = caughtError as BillPaymentError
        if (!shouldReconcileBillPaymentError(error)) {
          uploadAttemptRef.current = null
        }
        updateState({ phase: 'editable', error })
        return
      }

      if (session !== sessionRef.current) return

      const request = buildConfirmBillPaymentRequest(draft, attachment?.id ?? null)
      const attempt = confirmAttemptRef.current ?? {
        request,
        idempotencyKey: createBillPaymentIdempotencyKey(),
      }
      confirmAttemptRef.current = attempt

      let details: BillPaymentDetailsResponse
      try {
        details = await confirmPayment.mutateAsync(attempt)
      } catch (caughtError) {
        if (session !== sessionRef.current) return
        const error = caughtError as BillPaymentError
        if (shouldReconcileBillPaymentError(error)) {
          updateState({
            phase: 'confirm-unknown',
            error,
            isMfaOpen: false,
          })
        } else {
          confirmAttemptRef.current = null
          updateState({ phase: 'editable', error, isMfaOpen: false })
        }
        return
      }

      if (session !== sessionRef.current) return
      confirmAttemptRef.current = null
      const paymentId = details.payment?.id?.trim()
      if (
        details.payment?.state !== 'AWAITING_AUTHORIZATION' ||
        !details.authorization
      ) {
        // A successful confirm can have associated the attachment even when
        // the response is malformed. Never expose it to unattached cleanup.
        uploadAttemptRef.current = null
        if (!paymentId) {
          replaceState({
            phase: 'terminal',
            error: malformedResponseError(true),
            isMfaOpen: false,
            isSealed: true,
          })
          return
        }
        replaceState({
          phase: 'reconciling',
          details,
          error: malformedResponseError(),
          isMfaOpen: false,
          isSealed: true,
        })
        try {
          await reconcileDetails(details, session)
        } catch {
          // The reconciliation helper has already made the terminal outcome
          // visible without leaving this session interaction-locked.
        }
        return
      }

      // The server has associated the attachment with the payment. It is no
      // longer an unattached client-owned resource and must never be deleted.
      uploadAttemptRef.current = null
      replaceState({
        phase: 'awaiting-authorization',
        details,
        error: undefined,
        isMfaOpen: true,
        isSealed: true,
      })
    },
    [
      confirmPayment,
      discardUnattachedFile,
      replaceState,
      reconcileDetails,
      updateState,
      upload,
    ],
  )

  const reconcile = useCallback(async (): Promise<BillPaymentDetailsResponse> => {
    const details = stateRef.current.details
    if (!details) {
      throw new Error('Cannot reconcile a payment before it has been created')
    }
    const result = await reconcileDetails(details, sessionRef.current)
    if (result.kind === 'unavailable') throw result.error
    return result.details
  }, [reconcileDetails])

  const reconcileSchedule = useCallback(
    async (error: BillPaymentError): Promise<BillPaymentReconciliationResult> => {
      const details = stateRef.current.details
      if (!details) {
        throw new Error('Cannot reconcile a schedule before payment creation')
      }
      return reconcileDetails(details, sessionRef.current, 'schedule', error)
    },
    [reconcileDetails],
  )

  const deliveryUpdateFor = useCallback(
    (details: BillPaymentDetailsResponse): BillPaymentDeliveryUpdate | undefined => {
      const authorization = details.authorization
      if (!authorization) return undefined
      deliverySequenceRef.current += 1
      return {
        sequence: deliverySequenceRef.current,
        deliveryState: authorization.deliveryState,
        destinationHint: authorization.destinationHint,
      }
    },
    [],
  )

  const resend = useCallback(async (): Promise<BillPaymentSecondaryActionResult> => {
    const details = stateRef.current.details
    const authorization = details?.authorization
    if (!details || !authorization) {
      throw new Error('Cannot resend without a pending authorization')
    }
    const session = sessionRef.current
    const attempt = resendAttemptRef.current ?? {
      paymentId: details.payment.id,
      revision: details.payment.revision,
      authorizationId: authorization.authorizationId,
      idempotencyKey: createBillPaymentIdempotencyKey(),
    }
    resendAttemptRef.current = attempt
    try {
      const nextDetails = await resendAuthorization.mutateAsync(attempt)
      if (session === sessionRef.current) {
        resendAttemptRef.current = null
        restartAttemptRef.current = null
        replaceState({
          ...stateRef.current,
          phase: 'awaiting-authorization',
          details: nextDetails,
          error: undefined,
          isMfaOpen: true,
          isSealed: true,
          deliveryUpdate: deliveryUpdateFor(nextDetails),
        })
      }
      return { kind: 'updated', details: nextDetails }
    } catch (caughtError) {
      if (session !== sessionRef.current) throw caughtError
      const error = caughtError as BillPaymentError
      if (!shouldReconcileBillPaymentError(error)) {
        resendAttemptRef.current = null
        throw error
      }
      const result = await reconcileDetails(details, session, 'secondary', error)
      if (result.kind === 'unavailable') return result
      if (result.kind !== 'authorization') {
        return { kind: 'terminal', details: result.details }
      }
      if (result.details.authorization?.state === 'EXPIRED') {
        resendAttemptRef.current = null
        restartAttemptRef.current = null
        return { kind: 'expired', details: result.details }
      }
      if (
        result.sameAttempt &&
        authorizationSnapshot(result.details) === authorizationSnapshot(details)
      ) {
        return { kind: 'unchanged', details: result.details }
      }
      resendAttemptRef.current = null
      restartAttemptRef.current = null
      updateState({ deliveryUpdate: deliveryUpdateFor(result.details) })
      return { kind: 'updated', details: result.details }
    }
  }, [
    deliveryUpdateFor,
    reconcileDetails,
    replaceState,
    resendAuthorization,
    updateState,
  ])

  const restart = useCallback(async (): Promise<BillPaymentSecondaryActionResult> => {
    const details = stateRef.current.details
    if (!details) throw new Error('Cannot restart before payment creation')
    const session = sessionRef.current
    const attempt = restartAttemptRef.current ?? {
      paymentId: details.payment.id,
      revision: details.payment.revision,
      idempotencyKey: createBillPaymentIdempotencyKey(),
    }
    restartAttemptRef.current = attempt
    try {
      const nextDetails = await restartAuthorization.mutateAsync(attempt)
      if (session === sessionRef.current) {
        restartAttemptRef.current = null
        resendAttemptRef.current = null
        replaceState({
          phase: 'awaiting-authorization',
          details: nextDetails,
          error: undefined,
          isMfaOpen: true,
          isSealed: true,
          deliveryUpdate: deliveryUpdateFor(nextDetails),
        })
      }
      return { kind: 'updated', details: nextDetails }
    } catch (caughtError) {
      if (session !== sessionRef.current) throw caughtError
      const error = caughtError as BillPaymentError
      if (!shouldReconcileBillPaymentError(error)) {
        restartAttemptRef.current = null
        throw error
      }
      const result = await reconcileDetails(details, session, 'secondary', error)
      if (result.kind === 'unavailable') return result
      if (result.kind !== 'authorization') {
        return { kind: 'terminal', details: result.details }
      }
      if (result.details.authorization?.state === 'EXPIRED') {
        restartAttemptRef.current = null
        resendAttemptRef.current = null
        return { kind: 'expired', details: result.details }
      }
      if (
        result.sameAttempt &&
        authorizationSnapshot(result.details) === authorizationSnapshot(details)
      ) {
        return { kind: 'unchanged', details: result.details }
      }
      restartAttemptRef.current = null
      resendAttemptRef.current = null
      updateState({ deliveryUpdate: deliveryUpdateFor(result.details) })
      return { kind: 'updated', details: result.details }
    }
  }, [
    deliveryUpdateFor,
    reconcileDetails,
    replaceState,
    restartAuthorization,
    updateState,
  ])

  const openMfa = useCallback((): void => {
    if (
      stateRef.current.isSealed &&
      stateRef.current.phase === 'awaiting-authorization' &&
      (stateRef.current.details?.authorization?.state === 'PENDING' ||
        stateRef.current.details?.authorization?.state === 'EXPIRED')
    ) {
      updateState({ isMfaOpen: true })
    }
  }, [updateState])

  const closeMfa = useCallback((): void => {
    updateState({ isMfaOpen: false })
  }, [updateState])

  const acceptScheduled = useCallback(
    (payment: BillPaymentResponse): void => {
      replaceState({
        phase: 'terminal',
        details: stateRef.current.details
          ? { ...stateRef.current.details, payment }
          : undefined,
        error: undefined,
        isMfaOpen: false,
        isSealed: true,
      })
    },
    [replaceState],
  )

  const acceptTerminalError = useCallback(
    async (error: BillPaymentError): Promise<void> => {
      if (
        error.errorCode === 'AUTHORIZATION_CODE_INVALID' &&
        (error.attemptsRemaining ?? 1) <= 0
      ) {
        updateState({ phase: 'terminal', error, isMfaOpen: false, isSealed: true })
        return
      }
      if (shouldReconcileBillPaymentError(error) && stateRef.current.details) {
        updateState({ isMfaOpen: false, error })
        try {
          await reconcile()
        } catch {
          // `reconcile` has already put the flow in its fail-closed terminal state.
        }
        return
      }
      updateState({ phase: 'terminal', error, isMfaOpen: false, isSealed: true })
    },
    [reconcile, updateState],
  )

  const reset = useCallback((): void => {
    sessionRef.current += 1
    cleanupOperationRef.current += 1
    uploadAttemptRef.current = null
    confirmAttemptRef.current = null
    resendAttemptRef.current = null
    restartAttemptRef.current = null
    upload.reset()
    removeAttachment.reset()
    confirmPayment.reset()
    resendAuthorization.reset()
    restartAuthorization.reset()
    deliverySequenceRef.current = 0
    replaceState(initialState)
  }, [
    confirmPayment,
    removeAttachment,
    replaceState,
    resendAuthorization,
    restartAuthorization,
    upload,
  ])

  return {
    ...state,
    isInteractionLocked:
      state.phase === 'confirm-unknown' ||
      state.phase === 'reconciling' ||
      state.phase === 'schedule-unknown',
    confirm,
    openMfa,
    closeMfa,
    resend,
    restart,
    reconcile,
    reconcileSchedule,
    discardUnattachedFile,
    acceptScheduled,
    acceptTerminalError,
    reset,
  }
}
